import { Link } from "react-router-dom";
import { ArrowUpRight, Layers, ShieldCheck, Building2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Icon } from "@/components/common/Icon";
import { SectionHeading } from "@/components/common/Reveal";
import { INDUSTRIES } from "@/data/site";
import { CATEGORIES } from "@/data/products";

export const FactsBar = ({ c }) => (
  <section className="border-b border-slate-200 bg-white" data-testid="category-facts">
    <div className="container-x grid grid-cols-1 divide-y divide-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {[[Layers, `${c.products.length} product lines`, "Supplied, installed & maintained"], [ShieldCheck, c.standards.join(" · "), "Referenced standards"], [Building2, `${c.industries.length} industries`, "Typical deployments"]].map(([I, t, s]) => (
        <div key={s} className="flex items-center gap-4 py-6 sm:px-8 first:sm:pl-0">
          <I className="h-5 w-5 shrink-0" style={{ color: c.accent }} />
          <div><p className="text-sm font-semibold text-slate-900">{t}</p><p className="text-xs text-slate-500">{s}</p></div>
        </div>
      ))}
    </div>
  </section>
);

export const WhereUsed = ({ c }) => (
  <section className="bg-[#F4F6F2] py-20" data-testid="category-where-used">
    <div className="container-x">
      <SectionHeading eyebrow="Where it's used" title={`${c.name} across industries`} />
      <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {c.industries.map((name) => {
          const ind = INDUSTRIES.find((i) => i.name === name);
          return (
            <div key={name} className="card-base flex items-start gap-4 p-5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#123634]"><Icon name={ind?.icon} className="h-4 w-4 text-white" /></span>
              <div><p className="font-semibold text-slate-900">{name}</p><p className="mt-1 text-xs text-slate-500">{ind?.risk}</p></div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export const CategoryFaqs = ({ faqs }) => (
  <section className="bg-white py-20" data-testid="category-faqs">
    <div className="container-x grid gap-10 lg:grid-cols-3">
      <SectionHeading eyebrow="FAQs" title="Questions procurement teams ask" />
      <Accordion type="single" collapsible className="lg:col-span-2">
        {faqs.map(([q, a], i) => (
          <AccordionItem key={q} value={`faq-${i}`}>
            <AccordionTrigger className="text-left text-base font-semibold text-slate-900" data-testid={`category-faq-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-sm leading-relaxed text-slate-600">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const RelatedCategories = ({ current }) => {
  const i = CATEGORIES.findIndex((c) => c.slug === current.slug);
  const related = [1, 2, 3].map((n) => CATEGORIES[(i + n) % CATEGORIES.length]);
  return (
    <section className="border-t border-slate-200 bg-white py-16" data-testid="category-related">
      <div className="container-x">
        <p className="eyebrow">Explore more categories</p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {related.map((c) => (
            <Link key={c.slug} to={`/solutions/${c.slug}`} className="card-base group flex min-w-0 items-center gap-4 p-4" data-testid={`related-category-${c.slug}`}>
              <img src={c.products[0].image} alt={c.name} className="h-16 w-16 rounded-lg bg-white object-contain mix-blend-multiply" />
              <div className="min-w-0 flex-1"><p className="truncate font-semibold text-slate-900">{c.name}</p><p className="text-xs text-slate-500">{c.products.length} products</p></div>
              <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
